import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Radio, Lock, AlertTriangle } from "lucide-react";
import Hls from "hls.js";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function ChannelPlayer({ channel }) {
  const videoRef = useRef(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setError(false);
    if (!channel?.source_url || channel.source_type !== "hls" || !videoRef.current) return;
    const video = videoRef.current;
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = channel.source_url;
      return;
    }
    if (!Hls.isSupported()) {
      setError(true);
      return;
    }
    const hls = new Hls();
    hls.attachMedia(video);
    hls.on(Hls.Events.MEDIA_ATTACHED, () => hls.loadSource(channel.source_url));
    hls.on(Hls.Events.ERROR, (_e, data) => {
      if (!data.fatal) return;
      if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
      else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
      else {
        setError(true);
        hls.destroy();
      }
    });
    return () => hls.destroy();
  }, [channel?.id, channel?.source_url, channel?.source_type]);

  if (error) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
        <AlertTriangle size={36} className="text-gold mb-3" />
        <p className="font-mono-imn text-xs tracking-[0.3em] text-white/50 uppercase">Stream unavailable</p>
        <p className="text-white/40 text-sm mt-2">This channel is momentarily off — try another.</p>
      </div>
    );
  }
  if (channel.source_type === "hls") {
    return <video ref={videoRef} className="w-full h-full" controls autoPlay playsInline data-testid="live-player" />;
  }
  return (
    <iframe
      className="w-full h-full"
      src={channel.source_url}
      title={channel.name}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      data-testid="live-player"
    />
  );
}

export function LiveCable({ onGetPass }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [activeId, setActiveId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/live");
      setData(data);
      const firstLive = data.channels.find((c) => c.is_live) || data.channels[0];
      if (firstLive) setActiveId((id) => id || firstLive.id);
    } catch {}
  };
  useEffect(() => {
    load();
  }, [user]);

  if (!data || !data.channels?.length) return null;

  const active = data.channels.find((c) => c.id === activeId) || data.channels[0];
  const canWatch = data.unlocked && active?.is_live && active?.source_url;
  const anyLive = data.channels.some((c) => c.is_live);

  return (
    <section id="live" className="px-6 md:px-16 py-16 border-t border-white/10" data-testid="live-section">
      <div className="flex items-center gap-3 mb-6">
        <span className="relative flex h-3 w-3">
          {anyLive && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />}
          <span className={`relative inline-flex rounded-full h-3 w-3 ${anyLive ? "bg-red-500" : "bg-white/30"}`} />
        </span>
        <p className="font-mono-imn text-xs tracking-[0.3em] text-gold uppercase">
          {data.channels.length} Channels{anyLive ? " · Live Now" : " · Off Air"}
        </p>
      </div>
      <h2 className="font-display text-4xl md:text-6xl font-bold uppercase tracking-tight mb-8 flex items-center gap-4">
        <Radio className="text-gold" size={40} /> Local Cable
      </h2>

      {/* Player */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="relative aspect-video max-w-5xl border border-white/10 bg-[#0A0A0A] overflow-hidden"
      >
        {canWatch ? (
          <ChannelPlayer channel={active} />
        ) : !data.unlocked ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <Lock size={40} className="text-gold mb-4" />
            <p className="font-mono-imn text-xs tracking-[0.3em] text-gold uppercase mb-3">
              Live Pass · {data.price || "$7.99/mo"}
            </p>
            <h3 className="font-display text-2xl md:text-3xl font-bold uppercase mb-2">
              {data.channels.length} Local Cable Channels — Separate Pass
            </h3>
            <p className="text-white/50 text-sm max-w-md mb-6">
              The Local Cable lineup isn't included with your standard subscription — unlock all channels with a Live Pass for {data.price || "$7.99/mo"}.
            </p>
            <button
              onClick={onGetPass}
              data-testid="live-getpass-btn"
              className="bg-gold text-black font-semibold px-8 py-3 text-sm uppercase tracking-wider hover:opacity-90 active:scale-95 transition-all"
            >
              Get Live Pass · {data.price || "$7.99/mo"}
            </button>
          </div>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <Radio size={40} className="text-white/30 mb-4" />
            <p className="font-mono-imn text-xs tracking-[0.3em] text-white/40 uppercase">Off Air</p>
            <p className="text-white/40 text-sm mt-2">This channel is currently off air — pick another below.</p>
          </div>
        )}
      </motion.div>

      {/* Channel guide */}
      <div className="mt-6">
        <p className="font-mono-imn text-[11px] tracking-[0.3em] text-white/40 uppercase mb-4">Channel Guide</p>
        <div className="flex gap-4 overflow-x-auto hide-scrollbar pb-2">
          {data.channels.map((ch) => (
            <button
              key={ch.id}
              onClick={() => setActiveId(ch.id)}
              data-testid={`channel-${ch.id}`}
              className={`group relative shrink-0 w-40 md:w-48 text-left transition-all ${
                active?.id === ch.id ? "opacity-100" : "opacity-60 hover:opacity-100"
              }`}
            >
              <div className={`relative aspect-square overflow-hidden border ${active?.id === ch.id ? "border-gold" : "border-white/10"}`}>
                {ch.logo ? (
                  <img src={ch.logo} alt={ch.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-[#0A0A0A] font-display text-xl uppercase">{ch.name}</div>
                )}
                {ch.is_live && (
                  <span className="absolute top-2 left-2 flex items-center gap-1 bg-red-600 text-white text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5">
                    ● Live
                  </span>
                )}
              </div>
              <p className="font-display text-sm uppercase mt-2 truncate group-hover:text-gold transition-colors">{ch.name}</p>
              <p className="font-mono-imn text-[10px] tracking-widest text-white/40 uppercase">{ch.category}</p>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
